import {clamp} from '@benchling/js-utils';

import {NOTE_PADDING} from 'notebook/notes/NoteConstants';
import {TABLE_HEADER_HEIGHT} from 'notebook/tables/TableConstants';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';

import {GRID_CONTAINED_IMAGE_PADDING, MIN_ITEM_WIDTH, NotePartGrid} from '../NotePartGridGroupConstants';
import {computeRowsForFlowLayout, DraggableFlowItem} from './computeRowsForFlowLayout';

/**
 * Vertical chrome around each grid item's wrapper.
 * Derived from the `itemWrapper` style in DraggableItem.tsx:
 * - paddingTop: 28px (drag header area)
 * - padding: 4px (bottom; the 4px all-sides padding is overridden on top by paddingTop)
 * - border: 1px solid (top + bottom = 2px, with box-sizing: border-box)
 *
 * Total: 28 + 4 + 2 = 34px
 */
export const ITEM_VERTICAL_CHROME = 34;

/**
 * Default container width used for virtualized height calculation.
 * This matches the default used in NotePartGrid.tsx.
 */
export const DEFAULT_CONTAINER_WIDTH = 700;

/**
 * Height of the empty-state drop zone (border-box).
 * From the `emptyState` style in NotePartGrid.tsx: minHeight: 200px.
 * With global border-box, this already includes padding and border.
 */
export const EMPTY_GRID_HEIGHT = 200;

/**
 * Height of the GroupTitleBar header rendered above populated grids.
 * Reuses TABLE_HEADER_HEIGHT since GroupTitleBar shares the same height
 * as the table title bar.
 */
export const GRID_HEADER_HEIGHT = TABLE_HEADER_HEIGHT;

interface VirtualizedHeightOptions {
  containerWidth?: number;
}

/**
 * Computes the estimated virtualized height of a NotePartGrid based on its contained items.
 *
 * The algorithm:
 * 1. Compute the row layout using computeRowsForFlowLayout
 * 2. For each row, find the tallest item (estimating height from computedWidth using aspect ratio)
 * 3. Sum all row heights plus vertical spacing
 *
 * This provides a rough pixel height suitable for virtualization/skeleton rendering
 * before the actual images are loaded.
 */
export function computeNotePartGridVirtualizedHeight(
  group: NotePartGrid,
  options: VirtualizedHeightOptions = {}
): number {
  const {containedNoteParts} = group;

  if (containedNoteParts.length === 0) {
    // The empty state renders a dashed drop zone (emptyState style in NotePartGrid.tsx).
    // With global border-box, EMPTY_GRID_HEIGHT already includes padding and border.
    // SelectableNotePart adds NOTE_PADDING top and bottom.
    return EMPTY_GRID_HEIGHT + NOTE_PADDING * 2;
  }

  const containerWidth = options.containerWidth ?? DEFAULT_CONTAINER_WIDTH;

  // Calculate available row width (same logic as NotePartGrid.tsx).
  const availableRowWidth = containerWidth - DND_BLOCK_SPACING * 2 - GRID_CONTAINED_IMAGE_PADDING;
  const maxItemWidth = Math.max(availableRowWidth, MIN_ITEM_WIDTH);

  // Use the same row layout computation as the actual NotePartGrid component
  const rows = computeRowsForFlowLayout(
    containedNoteParts,
    availableRowWidth,
    MIN_ITEM_WIDTH,
    maxItemWidth,
    0,
    DND_BLOCK_SPACING
  );

  // Calculate height: header + grid canvas rows + spacing.
  // DnDCanvas adds paddingTop of DND_BLOCK_SPACING; the last DnDBlock's marginBottom
  // provides the equivalent bottom spacing.
  let totalHeight = GRID_HEADER_HEIGHT + DND_BLOCK_SPACING * 2;

  for (let i = 0; i < rows.length; i++) {
    const rowHeight = computeRowHeight(rows[i], maxItemWidth);
    totalHeight += rowHeight;

    // Add spacing between rows (DND_BLOCK_SPACING marginBottom on each DnDBlock)
    if (i < rows.length - 1) {
      totalHeight += DND_BLOCK_SPACING;
    }
  }

  return totalHeight;
}

/**
 * Computes the height of a single row by finding the tallest item.
 * Each item's height is estimated as its width (assuming square 1:1 aspect ratio)
 * plus the vertical chrome (padding, border, drag header).
 *
 * NOTE: Image dimensions are not stored in the data model. Neither NotePart,
 * NoteLinkedFile, nor ExternalFile/Image models include height information.
 * Only itemWidth is persisted. We use a 1:1 aspect ratio as a neutral estimate
 * that doesn't favor landscape or portrait. The virtualization system caches
 * actual heights after first render, so this is only used on initial load.
 */
function computeRowHeight(row: Array<DraggableFlowItem>, maxItemWidth: number): number {
  if (row.length === 0) {
    return 0;
  }

  let maxHeight = 0;
  for (const item of row) {
    const clampedWidth = clamp(MIN_ITEM_WIDTH, maxItemWidth, item.computedWidth);
    // Estimate image height as equal to width (1:1 square aspect ratio)
    // Add vertical chrome for the DraggableItem wrapper
    const itemHeight = clampedWidth + ITEM_VERTICAL_CHROME;
    maxHeight = Math.max(maxHeight, itemHeight);
  }

  return maxHeight;
}
